import { useRef, useState } from "react";
import { FormParser, TFormButton } from "src/view/FormParser";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";

export interface TSelectOption {
	value: string;
	name: string;
}

/**
 * Declarative description of a "dependent select": the options of `field`
 * are (re)computed from the current value of `dependsOn`.
 *
 * `resolve` may be async (e.g. fetch cities from the backend) or sync
 * (e.g. pick a slice of values already present in the form template).
 */
export interface IFieldDependency {
	field: string;
	dependsOn: string;
	when?: (data: Record<string, any>) => boolean;
	resolve: (dependencyValue: unknown, data: Record<string, any>) => Promise<TSelectOption[]> | TSelectOption[];
}

interface IDependentFormParserProps {
	onSubmit: (data: any) => Promise<any>;
	onRequest: () => Promise<any>;
	dependencies: IFieldDependency[];
	primaryButton?: TFormButton | null;
	secondaryButton?: TFormButton | null;
	fullSizeButtons?: boolean;
	actionsSx?: RecursiveCSS;
	sx?: RecursiveCSS;
	scrollable?: boolean;
	/** Optional extra content rendered below the form. */
	renderExtra?: (data: Record<string, any>) => React.ReactNode;
	extraBeforeActions?: boolean;
	ignoreObserver?: boolean;
	/** Receives the current mutable form model after initial load and changes. */
	onDataChange?: (data: Record<string, any>) => void;
	/**
	 * Field names whose changes should refresh `renderExtra`. Without this the
	 * preview would re-render on every keystroke (and could steal input focus),
	 * so callers list only the fields the preview actually depends on.
	 */
	extraDeps?: string[];
	highlightedFields?: string[];
}

const unwrap = (result: any): Record<string, any> | null => {
	if (!result || typeof result !== "object") return null;
	if ("error" in result && result.error) return null;
	if ("data" in result && result.data && typeof result.data === "object") return result.data;
	return result;
};

const getAtPath = (data: Record<string, any>, path: string): any =>
	path.split(".").reduce((current, part) => current?.[part], data);

const setAtPath = (data: Record<string, any>, path: string, value: unknown) => {
	const parts = path.split(".");
	const field = parts.pop()!;
	let parent = data;
	for (const part of parts) {
		if (!parent[part] || typeof parent[part] !== "object") parent[part] = {};
		parent = parent[part];
	}
	parent[field] = value;
};

const applyOptions = (data: Record<string, any>, fieldPath: string, options: TSelectOption[]) => {
	const parts = fieldPath.split(".");
	const field = parts.pop()!;
	let parent = data;
	for (const part of parts) {
		if (!parent[part] || typeof parent[part] !== "object") parent[part] = {};
		parent = parent[part];
	}
	parent[`$__${field}_type`] = "select";
	parent[`$__${field}_values`] = options;
};

export const DependentFormParser = ({
	onSubmit,
	onRequest,
	dependencies,
	primaryButton,
	secondaryButton,
	fullSizeButtons,
	actionsSx,
	sx,
	scrollable,
	renderExtra,
	extraBeforeActions,
	ignoreObserver,
	onDataChange,
	extraDeps,
	highlightedFields,
}: IDependentFormParserProps) => {
	const dataRef = useRef<Record<string, any> | null>(null);
	// Last seen value of each watched field (dependency drivers + preview deps).
	const prevDeps = useRef<Record<string, unknown>>({});
	// Bumping this re-renders the wrapper (and thus the FormParser subtree),
	// which re-reads the mutated `$__<field>_values` meta and the preview.
	const [, setTick] = useState(0);
	const rerender = () => setTick((t) => t + 1);

	const wrappedRequest = async () => {
		const result = await onRequest();
		const data = unwrap(result);
		if (data) {
			dataRef.current = data;
			for (const dep of dependencies) {
				if (dep.when && !dep.when(data)) continue;
				const depValue = getAtPath(data, dep.dependsOn);
				prevDeps.current[dep.dependsOn] = depValue;
				if (depValue !== undefined && depValue !== null && depValue !== "") {
					try {
						applyOptions(data, dep.field, await dep.resolve(depValue, data));
					} catch {
						applyOptions(data, dep.field, []);
					}
				} else {
					applyOptions(data, dep.field, []);
				}
			}
			for (const field of extraDeps ?? []) prevDeps.current[field] = getAtPath(data, field);
			onDataChange?.(data);
		}
		return result;
	};

	const handleChange = async (data: any) => {
		const target = unwrap(data) ?? (data as Record<string, any>);
		if (!target || typeof target !== "object") return;
		dataRef.current = target;
		onDataChange?.(target);

		let changed = false;
		for (const dep of dependencies) {
			if (dep.when && !dep.when(target)) continue;
			const current = getAtPath(target, dep.dependsOn);
			if (current === prevDeps.current[dep.dependsOn]) continue;

			prevDeps.current[dep.dependsOn] = current;
			// Dependency changed → previously selected option is no longer valid.
			setAtPath(target, dep.field, "");
			if (current !== undefined && current !== null && current !== "") {
				try {
					applyOptions(target, dep.field, await dep.resolve(current, target));
				} catch {
					applyOptions(target, dep.field, []);
				}
			} else {
				applyOptions(target, dep.field, []);
			}
			changed = true;
		}

		// Refresh the preview only when a field it depends on actually changed,
		// so ordinary typing in other inputs does not force a re-render.
		if (renderExtra) {
			for (const field of extraDeps ?? []) {
				const current = getAtPath(target, field);
				if (current !== prevDeps.current[field]) {
					prevDeps.current[field] = current;
					changed = true;
				}
			}
		}

		if (changed) rerender();
	};

	return (
		<>
			<FormParser
				onRequest={wrappedRequest}
				onSubmit={onSubmit}
				onChange={handleChange}
				ignoreObserver={ignoreObserver}
				beforeActions={extraBeforeActions && renderExtra && dataRef.current ? renderExtra(dataRef.current) : undefined}
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
				fullSizeButtons={fullSizeButtons}
				actionsSx={actionsSx}
				highlightedFields={highlightedFields}
				sx={sx}
				scrollable={scrollable}
			/>
			{!extraBeforeActions && renderExtra && dataRef.current && renderExtra(dataRef.current)}
		</>
	);
};
